import { normalizeOperationOutcome } from '@medplum/core'
import type {
  Attachment,
  OperationOutcome,
  Patient,
  Reference,
} from '@medplum/fhirtypes'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import {
  type ChangeEvent,
  type DragEvent,
  type JSX,
  type MouseEvent,
  type ReactNode,
  type SyntheticEvent,
  useRef,
} from 'react'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { createAttachmentFromFile } from '#src/utils/createAttachmentFromFile.ts'
import {
  type DuplicateFile,
  findDuplicateFiles,
} from '#src/utils/findDuplicateFiles.ts'

export interface DuplicateCheck {
  participant: Reference<Patient>
  // Called once per selection that held duplicates, before the rest upload.
  // `uploadingCount` is how many files are still going to upload; when it is
  // 0, `onUpload` is never called.
  onDuplicates(duplicates: DuplicateFile[], uploadingCount: number): void
}

export interface CustomAttachmentButtonProps {
  readonly securityContext?: Reference
  // With `duplicateCheck`, `onUpload` receives the v1 document fingerprint of
  // exactly the attachments it is given, for stamping on the document.
  readonly onUpload: (
    attachment: Attachment[],
    meta?: { fingerprint: string }
  ) => void
  readonly duplicateCheck?: DuplicateCheck
  readonly onUploadStart?: (length: number) => void
  readonly onUploadProgress?: (e: ProgressEvent, index: number) => void
  readonly onUploadError?: (outcome: OperationOutcome) => void
  children(props: {
    disabled?: boolean
    onClick(e: MouseEvent): void
    onDrop(e: DragEvent): void
    onDragOver(e: DragEvent): void
  }): ReactNode
  readonly disabled?: boolean
}

/**
 * This component is designed to replace the AttachmentButton component in the medplum/react package.
 * It allows for multiple file uploads to take place concurrently, which is superior to the existing AttachmentButton component.
 * The children prop will render the button that will trigger the file input dialog.
 *
 * @param props - props for component, see the CustomAttachmentButtonProps interface for more information.
 * @returns JSX.Element
 */
export function CustomAttachmentButton(
  props: CustomAttachmentButtonProps
): JSX.Element {
  const medplum = useMedplum()
  const fileInputRef = useRef<HTMLInputElement>(null)

  function onClick(e: MouseEvent): void {
    killEvent(e)
    fileInputRef.current?.click()
  }

  function onDragOver(e: DragEvent): void {
    if (props.disabled) {
      return
    }
    killEvent(e)
  }

  async function onDrop(e: DragEvent): Promise<void> {
    if (props.disabled) {
      return
    }
    killEvent(e)
    await uploadFiles(e.dataTransfer.files)
  }

  async function onFileChange(e: ChangeEvent): Promise<void> {
    killEvent(e)
    const files = (e.target as HTMLInputElement).files
    if (!files) {
      return
    }

    await uploadFiles(files)

    /* Reset the file input value so the same file can be selected again. */
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  async function uploadFiles(files: FileList): Promise<void> {
    if (!files.length) {
      return
    }

    try {
      props.onUploadStart?.(files.length)

      let filesToUpload = Array.from(files)
      let fingerprint: string | undefined
      if (props.duplicateCheck) {
        const result = await findDuplicateFiles(filesToUpload, {
          search: (query) =>
            medplum.searchResourceClass(ExtendedDocumentReference, query),
          participant: props.duplicateCheck.participant,
        })
        if (result.duplicates.length > 0) {
          props.duplicateCheck.onDuplicates(
            result.duplicates,
            result.fresh.length
          )
        }
        if (result.fresh.length === 0) {
          return
        }
        filesToUpload = result.fresh
        fingerprint = result.freshFingerprint
      }

      const createdAttachments = await Promise.all(
        filesToUpload.map(processFile)
      ).then((attachments) => attachments.filter((f) => f !== null))

      if (createdAttachments.length !== filesToUpload.length) {
        throw new Error('Failed to upload all files')
      }

      if (fingerprint) {
        props.onUpload(createdAttachments, { fingerprint })
      } else {
        props.onUpload(createdAttachments)
      }
    } catch (err) {
      props.onUploadError?.(normalizeOperationOutcome(err))
    }
  }

  async function processFile(
    file: File,
    index: number
  ): Promise<Attachment | null> {
    if (!file) {
      return null
    }

    const fileName = file.name
    if (!fileName) {
      return null
    }

    return createAttachmentFromFile(file, {
      medplum,
      securityContext: props.securityContext,
      onProgress: (e) => props.onUploadProgress?.(e, index),
    })
  }

  return (
    <>
      {/* hidden input that manages uploading files, the `children` prop instead controls how this component is rendered */}
      <input
        disabled={props.disabled}
        multiple
        type="file"
        data-testid="upload-file-input"
        className="hidden"
        ref={fileInputRef}
        onChange={(e) => onFileChange(e)}
      />
      {props.children({
        onClick,
        onDrop: (e) => {
          void onDrop(e)
        },
        onDragOver,
        disabled: props.disabled,
      })}
    </>
  )
}

function killEvent(e: Event | SyntheticEvent): void {
  e.preventDefault()
  e.stopPropagation()
}
